import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Heading from "@atlaskit/heading";
import { CardClient, SmartCardProvider } from "@atlaskit/link-provider";
import { Stack, Text } from "@atlaskit/primitives-next";
import Skeleton from "@atlaskit/skeleton";

import { GenericErrorDisplay } from "@/components/fastshift/src/common/displays";
import { BASE_URL } from "@/components/fastshift/src/common/routes/constants";
import * as StateActions from "@/components/fastshift/src/state/actions";
import {
    useFastshiftDispatch,
    useFastshiftSelector
} from "@/components/fastshift/src/state/hook";
import { CustomerDomainEngagementExpectedError } from "@/libraries/bxp-web-stargate-client/src";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";

import { ConfluenceCardLinkSection } from "./confluence-card-link-section";
import {
    NoAccessErrorDisplay,
    NoEngagementErrorDisplay,
    UserNotProvisionedErrorDisplay
} from "./error-displays";
import { useMigrationsManagementPageAnalytics } from "./hooks";
import { JiraEmbeddedBoardSection } from "./jira-embedded-board-section";
import { messages } from "./messages";

const appEnvironment =
    process.env["NODE_ENV"] === "production" ? "production" : "staging";

const MigrationPlannerIntegration = () => {
    const intl = useIntl();

    const cardClient = new CardClient(
        appEnvironment,
        `${BASE_URL}/gateway/api`
    );

    const {
        data: engagementData,
        error: engagementError,
        pending: engagementPending
    } = useFastshiftSelector((state) => state.customerDomainEngagement);

    useMigrationsManagementPageAnalytics();

    const { domainId } = useParams<{ domainId: string }>();
    const stargateClient = useBxpWebStargateClient();
    const dispatch = useFastshiftDispatch();

    const handleRefreshEngagementEndpoint = () => {
        dispatch(
            StateActions.getCustomerDomainEngagementDetails({
                stargateClient,
                customerDomainId: domainId || ""
            })
        );
    };

    if (engagementPending) {
        return (
            <>
                <Skeleton
                    height="650px"
                    width="100%"
                    testId="migration-planner-loading-skeleton"
                    isShimmering
                />
                <Skeleton
                    height="90px"
                    width="100%"
                    testId="confluence-card-skeleton"
                    isShimmering
                />
            </>
        );
    }

    if (engagementError) {
        switch (engagementError) {
            case CustomerDomainEngagementExpectedError.NO_ENGAGEMENT:
                return <NoEngagementErrorDisplay />;
            case CustomerDomainEngagementExpectedError.USER_NOT_PROVISIONED:
                return <UserNotProvisionedErrorDisplay />;
            case CustomerDomainEngagementExpectedError.NO_ACCESS:
                return <NoAccessErrorDisplay />;
            default:
                return (
                    <GenericErrorDisplay
                        onRefresh={handleRefreshEngagementEndpoint}
                        customMessage={intl.formatMessage(
                            messages.genericErrorDescription
                        )}
                    />
                );
        }
    }

    if (!engagementData) {
        return (
            <GenericErrorDisplay
                onRefresh={handleRefreshEngagementEndpoint}
                customMessage={intl.formatMessage(
                    messages.genericErrorDescription
                )}
            />
        );
    }

    return (
        <SmartCardProvider client={cardClient}>
            <JiraEmbeddedBoardSection />
            <ConfluenceCardLinkSection />
        </SmartCardProvider>
    );
};

export const FastshiftMigrationsManagementPage = () => {
    return (
        <Stack space="space.300">
            <Stack space="space.100">
                <Heading size="large">
                    <FormattedMessage {...messages.pageHeading} />
                </Heading>
                <Text>
                    <FormattedMessage {...messages.pageHeadingSubtext} />
                </Text>
            </Stack>
            <MigrationPlannerIntegration />
        </Stack>
    );
};
